import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Search } from "lucide-react";
import api from "@/lib/api";

function fmtLong(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function yearOf(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return String(d.getFullYear());
}

export default function Journal() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    let active = true;
    api
      .get("/posts")
      .then(({ data }) => {
        if (!active) return;
        const sorted = [...data].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setPosts(sorted);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const categories = useMemo(() => {
    const set = new Set(["All"]);
    posts.forEach((p) => p.category && set.add(p.category));
    return Array.from(set);
  }, [posts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (category !== "All" && p.category !== category) return false;
      if (!q) return true;
      return (
        (p.title || "").toLowerCase().includes(q) ||
        (p.excerpt || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q)
      );
    });
  }, [posts, query, category]);

  const grouped = useMemo(() => {
    const groups = new Map();
    filtered.forEach((p) => {
      const y = yearOf(p.date);
      if (!groups.has(y)) groups.set(y, []);
      groups.get(y).push(p);
    });
    return Array.from(groups.entries());
  }, [filtered]);

  return (
    <section
      id="journal"
      data-testid="journal-section"
      className="relative py-16 md:py-20 lg:py-24 bg-black border-t border-white/5"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 text-sm text-zinc-500"
        >
          <span className="w-8 h-px bg-zinc-700" />
          <span>03 · Journal</span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="font-heading mt-5 text-3xl md:text-5xl lg:text-6xl font-light tracking-[-0.025em] leading-[1.05] text-white max-w-3xl"
        >
          Notes from the bench.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-5 max-w-2xl text-base md:text-lg text-zinc-400 font-light leading-relaxed"
        >
          A reading list, not a gallery. Just me thinking out loud about whatever
          I&apos;m building right now: the small wins, the spectacular fails, and the
          lessons that always seem to arrive after midnight.
        </motion.p>

        {/* Search + filter row */}
        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
          <div className="flex items-center gap-3 bg-white/[0.02] border border-white/10 focus-within:border-white/30 rounded-full px-4 py-2.5 max-w-md w-full transition-colors">
            <Search className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
            <input
              data-testid="journal-search-input"
              type="search"
              placeholder="Search the journal…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="bg-transparent outline-none text-sm flex-1 text-white placeholder:text-zinc-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`journal-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                  category === c
                    ? "bg-white text-black border-white"
                    : "bg-transparent text-zinc-300 hover:text-white border-white/10 hover:border-white/30"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Index / archive */}
        <div className="mt-10 md:mt-14">
          {loading ? (
            <div className="py-16 text-center text-zinc-500 text-sm">
              Loading posts…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-zinc-500 text-sm">
              No posts match those filters.
            </div>
          ) : (
            <div className="space-y-14 md:space-y-16">
              {grouped.map(([year, list]) => (
                <YearBlock key={year} year={year} posts={list} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function YearBlock({ year, posts }) {
  return (
    <div
      data-testid={`journal-year-${year}`}
      className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6 md:gap-12"
    >
      <div className="md:sticky md:top-28 self-start">
        <div className="font-heading text-5xl md:text-6xl font-light tracking-tight text-white leading-none">
          {year}
        </div>
        <div className="mt-2 text-sm text-zinc-500">
          {posts.length} {posts.length === 1 ? "entry" : "entries"}
        </div>
      </div>

      <ol className="divide-y divide-white/5 border-y border-white/5">
        {posts.map((p, i) => (
          <motion.li
            key={p.slug}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: i * 0.04 }}
            data-testid={`journal-post-${p.slug}`}
          >
            <Link
              to={`/blog/${p.slug}`}
              data-testid={`journal-post-link-${p.slug}`}
              className="group flex items-start md:items-center gap-4 md:gap-8 py-5 md:py-7 hover:bg-white/[0.015] transition-colors -mx-2 md:-mx-3 px-2 md:px-3 rounded-lg"
            >
              <div className="hidden md:block w-28 shrink-0 text-sm text-zinc-500 font-light">
                {fmtLong(p.date)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 text-sm text-zinc-500 md:hidden">
                  <span>{fmtLong(p.date)}</span>
                  {p.category && (
                    <>
                      <span className="w-1 h-1 rounded-full bg-zinc-700" />
                      <span>{p.category}</span>
                    </>
                  )}
                </div>
                <h3 className="font-heading text-xl md:text-2xl lg:text-[28px] font-light tracking-tight text-white leading-snug">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm md:text-base text-zinc-400 font-light line-clamp-2 leading-relaxed max-w-2xl">
                  {p.excerpt}
                </p>
              </div>

              <div className="hidden md:flex items-center gap-3 shrink-0 text-sm text-zinc-500">
                {p.category && (
                  <span className="bg-white/[0.03] border border-white/10 px-3 py-1 rounded-full">
                    {p.category}
                  </span>
                )}
                {p.read_time && <span className="w-16 text-right">{p.read_time}</span>}
                <ArrowUpRight
                  className="w-4 h-4 text-zinc-400 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                  strokeWidth={1.75}
                />
              </div>

              <ArrowUpRight
                className="md:hidden w-4 h-4 text-zinc-500 mt-2"
                strokeWidth={1.75}
              />
            </Link>
          </motion.li>
        ))}
      </ol>
    </div>
  );
}
